import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { aborted, blocked, failed } from 'flare-chat-core/contracts/chat/operation-result';
import type { ChatOperationResult } from 'flare-chat-core/contracts';
import type { ComposerInputIntent, ComposerInputProjection, InteractionPresentationReducer } from '../../projection/composer/contracts';
import type { ProductSendInput } from '../chat/contracts';
import { buildComposerInputSubmission, matchesInputProjection, resolveInputInteraction, type InputResponse } from './input-response';
import { reduceInteractionPresentation, resolveComposerInputPresentation } from '../../projection/composer/composerUiProjectionSelector.js';
import { buildActionInputSubmission } from './action-input.js';

type Result = ChatOperationResult<unknown>;
interface InputOptions {
  activeSessionId: string; userId?: string; projectId?: string; ready: boolean;
  projection: ComposerInputProjection; content: string; loading: boolean; submitting: boolean;
  actionInput: { pending: boolean; submit: (input: object) => Promise<Result> };
  onSend: (input?: ProductSendInput) => Promise<Result>;
  onRespondInput: (input: InputResponse) => Promise<Result>;
  onRetry: () => Promise<Result>;
}

const executeAutoResponse = async (snapshot: InputOptions, key: string, response: InputResponse,
  receipts: Map<string, { key: string; result: Result }>) => {
  const previous = receipts.get(key);
  if (previous?.key === key && previous.result.status === 'completed') return previous.result;
  // Only post-capture Core stages may retry its remembered request; preflight never records one.
  const retry = previous?.key === key && ['failed', 'partial'].includes(previous.result.status)
    && 'error' in previous.result
    && ['stream', 'appendExchange', 'loadSession'].includes(previous.result.error.operation ?? '');
  const result = await Promise.resolve().then(() => retry ? snapshot.onRetry() : snapshot.onRespondInput(response))
    .catch((error) => failed(error, 'submit_input'));
  receipts.set(key, { key, result });
  return result;
};

/** Instance command wiring only; Core owns streams and FLARE owns Run/input settlement. */
export const useInputResponse = (options: InputOptions) => {
  // FLARE disclosure state is presentation-only and scoped by the current question identity.
  const [dismissedInputKeys, setDismissedInputKeys] = useState<Set<string>>(() => new Set());
  const [presentations, dispatchPresentation] = useReducer<InteractionPresentationReducer>(reduceInteractionPresentation, new Map());
  // A view switch retires dispatch authority, not the scoped operation receipt.
  // The reducer fences settlement by question key and attempt; disposal alone ends publication.
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const inputKey = String(Object(options.projection.composerChooser).key ?? '');
  const presentationKey = JSON.stringify([options.projectId, options.userId, options.activeSessionId, inputKey]);
  const projection: ComposerInputProjection = useMemo(() => resolveComposerInputPresentation(
    options.projection, dismissedInputKeys.has(inputKey) ? inputKey : '', presentations.get(presentationKey),
  ), [dismissedInputKeys, inputKey, options.projection, presentationKey, presentations]);
  const current = useRef({ ...options, projection });
  current.current = { ...options, projection };
  const scope = useMemo(() => ({ active: true, sessionId: options.activeSessionId, projectId: options.projectId, userId: options.userId }), [options.activeSessionId, options.projectId, options.userId]);
  const currentScope = useRef(scope);
  currentScope.current = scope;
  useEffect(() => { scope.active = true; return () => { scope.active = false; }; }, [scope]);
  const pending = useRef(new Map<string, Promise<Result>>());
  const retries = useRef(new Map<string, { key: string; invoke: () => Promise<Result>; result?: Result; presentationKey: string }>());
  const previousAuto = useRef(new Map<string, { key: string; result: Result }>());
  const interaction = useMemo(() => resolveInputInteraction({ ready: options.ready,
    busy: options.loading || options.submitting || options.actionInput.pending, projection }),
  [options.ready, options.loading, options.submitting, options.actionInput.pending, projection]);

  const onRespondInput = useCallback((intent: ComposerInputIntent): Promise<Result> => {
    const snapshot = current.current;
    const owner = currentScope.current;
    if (!owner.active) return Promise.resolve(aborted());
    if (!matchesInputProjection(snapshot.projection, intent.projection)) return Promise.resolve(blocked('input_request_stale'));
    let response: InputResponse;
    try { response = buildComposerInputSubmission({ ...intent, projection: snapshot.projection }); }
    catch (error) { return Promise.resolve(failed(error, 'submit_input')); }
    const key = JSON.stringify([snapshot.projectId, snapshot.userId, snapshot.activeSessionId, snapshot.projection.protocol, snapshot.projection.requestKey, response.response_kind ?? 'answers', response.values, response.message]);
    const running = pending.current.get(key);
    if (running) return running;
    const availability = resolveInputInteraction({ ready: snapshot.ready,
      busy: snapshot.loading || snapshot.submitting || snapshot.actionInput.pending, projection: snapshot.projection });
    if (!availability.available) return Promise.resolve(blocked(availability.reason));
    const auto = () => executeAutoResponse(snapshot, key, response, previousAuto.current);
    const presentationKey = JSON.stringify([snapshot.projectId, snapshot.userId, snapshot.activeSessionId,
      Object(snapshot.projection.composerChooser).key ?? '']);
    const attempt = {};
    const receipt = { key: snapshot.projection.requestKey ?? '', presentationKey, invoke: auto, result: undefined as Result | undefined };
    const workflow = () => {
      if (response.response_kind === 'message') return Promise.resolve(blocked('action_input_invalid'));
      const submission = buildActionInputSubmission({ request: response.inputRequest,
        values: response.values, submittedBy: snapshot.userId });
      if (!submission) return Promise.resolve(blocked('action_input_invalid'));
      const command = { actionInputSubmission: submission, runId: submission.run_id };
      receipt.invoke = () => current.current.actionInput.submit(command);
      return snapshot.actionInput.submit(command);
    };
    const dispatch = new Map([['auto', auto], ['workflow', workflow]]).get(snapshot.projection.protocol ?? '');
    if (!dispatch) return Promise.resolve(blocked('input_request_stale'));
    // Capture one logical intent before any async callback; duplicates share its receipt.
    retries.current.set(JSON.stringify([snapshot.projectId, snapshot.userId, snapshot.activeSessionId]), receipt);
    dispatchPresentation({ type: 'submit', key: presentationKey, attempt });
    const executing = Promise.resolve().then(() => owner.active && currentScope.current === owner
      ? dispatch() : aborted()).catch((error) => failed(error, 'submit_input')).then((result) => {
      receipt.result = result;
      if (mounted.current) dispatchPresentation({
        type: 'settle', key: presentationKey, attempt, result,
      });
      return result;
    });
    const settled = executing.finally(() => { pending.current.delete(key); });
    pending.current.set(key, settled);
    return settled;
  }, []);

  const onSend = useCallback((input: ProductSendInput = {}): Promise<Result> => {
    const snapshot = current.current;
    if (!currentScope.current.active) return Promise.resolve(aborted());
    const dispatch = new Map([
      ['ordinary', () => {
        const availability = resolveInputInteraction({ ready: snapshot.ready,
          busy: snapshot.loading || snapshot.submitting || snapshot.actionInput.pending, projection: snapshot.projection });
        if (!availability.available) return Promise.resolve(blocked(availability.reason));
        retries.current.delete(JSON.stringify([snapshot.projectId, snapshot.userId, snapshot.activeSessionId]));
        return snapshot.onSend(input);
      }],
      ['text', () => onRespondInput({ content: input.content ?? snapshot.content })],
      ['message', () => onRespondInput({ content: input.content ?? snapshot.content })],
      ['chooser', () => Promise.resolve(blocked('input_required'))],
      ['invalid', () => Promise.resolve(blocked(snapshot.projection.error?.code ?? 'input_request_stale'))],
    ]);
    return dispatch.get(snapshot.projection.kind)!();
  }, [onRespondInput]);
  const onRetry = useCallback((): Promise<Result> => {
    const snapshot = current.current;
    const owner = currentScope.current;
    if (!owner.active) return Promise.resolve(aborted());
    const key = JSON.stringify([snapshot.projectId, snapshot.userId, snapshot.activeSessionId, 'retry_input']);
    const running = pending.current.get(key);
    if (running) return running;
    const availability = resolveInputInteraction({ ready: snapshot.ready,
      busy: snapshot.loading || snapshot.submitting || snapshot.actionInput.pending });
    if (!availability.available) return Promise.resolve(blocked(availability.reason));
    const receipt = retries.current.get(JSON.stringify([snapshot.projectId, snapshot.userId, snapshot.activeSessionId]));
    if (receipt?.result?.status === 'completed') return Promise.resolve(receipt.result);
    if (receipt && receipt.result && ['failed', 'partial'].includes(receipt.result.status)) {
      const refreshOnly = receipt.result.status === 'partial' && receipt.result.error.operation === 'refreshSession';
      if (receipt.key !== snapshot.projection.requestKey && !refreshOnly) return Promise.resolve(blocked('input_request_stale'));
      const attempt = {};
      dispatchPresentation({ type: 'submit', key: receipt.presentationKey, attempt });
      const executing = Promise.resolve().then(() => owner.active && currentScope.current === owner
        ? receipt.invoke() : aborted()).catch((error) => failed(error, 'submit_input')).then((result) => {
        receipt.result = result;
        if (mounted.current) dispatchPresentation({
          type: 'settle', key: receipt.presentationKey, attempt, result,
        });
        return result;
      });
      const settled = executing.finally(() => { pending.current.delete(key); });
      pending.current.set(key, settled);
      return settled;
    }
    if (snapshot.projection.kind !== 'ordinary') return Promise.resolve(blocked('input_required'));
    return snapshot.onRetry();
  }, []);
  const onComposerChooserDismiss = useCallback(() => {
    const snapshot = current.current;
    const chooser = Object(snapshot.projection.composerChooser);
    if (!currentScope.current.active || snapshot.projection.protocol !== 'auto' || chooser.dismissible !== true) return;
    const key = String(chooser.key ?? '');
    if (!key) return;
    setDismissedInputKeys((keys) => new Set([...keys, key]));
  }, []);
  return useMemo(() => ({ ...interaction, projection, error: projection.error ?? null,
    onSend, onRespondInput, onRetry, onComposerChooserDismiss }),
  [interaction, projection, onRespondInput, onRetry, onSend, onComposerChooserDismiss]);
};
